/**
 * CycleContextBlock — operational coaching context on ClientDetailPage header.
 *
 * UX rules (from product direction):
 *   * Calm, EMR / coaching notebook vibe — NOT a metric card.
 *   * No giant numbers, no charts, no colored tiles, no gamification.
 *   * Compact, readable, persistent.
 *   * Trainer/admin only — English copy.
 *
 * Surfaces:
 *   * Current Cycle (focus + Week N/M)
 *   * 🪶 Last Deload: N weeks ago
 *   * 📈 Continuity: <tier>
 *   * 📅 Suggested Deload: 1–2 weeks (when continuity is non-normal)
 *
 * Empty state: "No active cycle" + "Create cycle" CTA.
 */
import React, { useState, useEffect } from 'react';
import { FOCUS_LABELS, formatWeekN, daysSince, useCurrentCycle } from './useCycles';
import { CycleCreateModal } from './CycleCreateModal';
import { CycleCloseModal } from './CycleCloseModal';

const TIER_LABEL = {
  normal: 'Normal',
  attention: 'Attention',
  review: 'Review',
};

const TIER_DOT = {
  normal: 'bg-emerald-400',
  attention: 'bg-amber-400',
  review: 'bg-purple-400',
};

const formatDeloadAgo = (lastDeloadAt) => {
  const days = daysSince(lastDeloadAt);
  if (days == null) return null;
  if (days < 0) return 'Today';
  if (days < 7) return `${days}d ago`;
  const weeks = Math.round(days / 7);
  return `${weeks} weeks ago`;
};

/**
 * Map continuity tier to a suggested deload window. Returns null for normal
 * tier — the trainer doesn't need a prompt when accumulation is healthy.
 */
const suggestedDeloadFor = (tier) => {
  if (tier === 'review') return '1 week';
  if (tier === 'attention') return '1–2 weeks';
  return null;
};

export const CycleContextBlock = ({ clientId, continuity, onChange, refreshKey = 0 }) => {
  const { cycle, loading, create, close, refresh } = useCurrentCycle(clientId);
  const [showCreate, setShowCreate] = useState(false);
  const [showClose, setShowClose] = useState(false);

  // External refresh signal — parent (ClientDetailPage) bumps this when
  // cycles change in the Cycles tab so the header reflects the new state.
  useEffect(() => {
    if (refreshKey > 0) refresh();
  }, [refreshKey, refresh]);

  const handleCreate = async (payload) => {
    await create(payload);
    if (onChange) onChange();
  };

  const handleClose = async (payload) => {
    await close(payload);
    setShowClose(false);
    if (onChange) onChange();
  };

  const lastDeloadAgo = formatDeloadAgo(continuity?.last_deload_at);
  const tier = continuity?.tier;
  const suggested = suggestedDeloadFor(tier);

  return (
    <>
      <section
        className="border border-slate-700/60 bg-slate-900/40 rounded-md px-4 py-3"
        data-testid="cycle-context-block"
      >
        {loading ? (
          <div className="text-slate-500 text-sm">Loading cycle…</div>
        ) : cycle ? (
          <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
            {/* Cycle headline */}
            <div className="flex-1 min-w-[180px]">
              <div className="text-slate-400 text-[11px] uppercase tracking-wider mb-0.5">
                Current Cycle
              </div>
              <div className="text-white text-sm">
                <span className="font-semibold" data-testid="cycle-focus-label">
                  {FOCUS_LABELS[cycle.focus] || cycle.focus}
                </span>
                <span className="text-slate-500 mx-1.5">·</span>
                <span className="text-slate-200" data-testid="cycle-week-label">
                  {formatWeekN(cycle)}
                </span>
              </div>
              {cycle.focus_note && (
                <div className="text-slate-400 text-xs mt-0.5 italic">{cycle.focus_note}</div>
              )}
            </div>

            {/* Last Deload */}
            <div className="min-w-[120px]">
              <div className="text-slate-400 text-[11px] uppercase tracking-wider mb-0.5">
                🪶 Last Deload
              </div>
              <div className="text-slate-200 text-sm" data-testid="cycle-last-deload">
                {lastDeloadAgo || 'None on record'}
              </div>
            </div>

            {/* Continuity */}
            {continuity && (
              <div className="min-w-[120px]">
                <div className="text-slate-400 text-[11px] uppercase tracking-wider mb-0.5">
                  📈 Continuity
                </div>
                <div
                  className="text-slate-200 text-sm flex items-center gap-1.5"
                  data-testid={`cycle-continuity-${tier || 'normal'}`}
                >
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${TIER_DOT[tier] || TIER_DOT.normal}`} />
                  {TIER_LABEL[tier] || TIER_LABEL.normal}
                  <span className="text-slate-500">
                    · {continuity.block_weeks ?? 0}w accumulated
                  </span>
                </div>
              </div>
            )}

            {/* Suggested Deload (only when non-normal) */}
            {suggested && (
              <div className="min-w-[120px]">
                <div className="text-slate-400 text-[11px] uppercase tracking-wider mb-0.5">
                  📅 Suggested Deload
                </div>
                <div className="text-amber-200 text-sm" data-testid="cycle-suggested-deload">
                  {suggested}
                </div>
              </div>
            )}

            {/* Manage */}
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => setShowClose(true)}
                className="text-xs px-2 py-1 rounded text-emerald-300 hover:text-emerald-200 hover:bg-emerald-900/30"
                data-testid="cycle-close-btn"
              >
                Close & report
              </button>
              <button
                onClick={() => setShowCreate(true)}
                className="text-xs px-2 py-1 rounded text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                data-testid="cycle-replace-btn"
              >
                New cycle →
              </button>
            </div>
          </div>
        ) : (
          /* Empty state */
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-slate-400 text-[11px] uppercase tracking-wider mb-0.5">
                Current Cycle
              </div>
              <div className="text-slate-300 text-sm" data-testid="cycle-empty-state">
                No active cycle
              </div>
              {continuity?.last_deload_at && (
                <div className="text-slate-500 text-xs mt-1">
                  🪶 Last Deload: {lastDeloadAgo}
                </div>
              )}
            </div>
            <button
              onClick={() => setShowCreate(true)}
              className="text-sm px-3 py-1.5 rounded border border-emerald-600/60 text-emerald-300 hover:bg-emerald-600/15"
              data-testid="cycle-create-cta"
            >
              Create cycle
            </button>
          </div>
        )}
      </section>

      <CycleCreateModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreate={handleCreate}
      />
      <CycleCloseModal
        open={showClose}
        cycle={cycle}
        onClose={() => setShowClose(false)}
        onConfirm={handleClose}
      />
    </>
  );
};

export default CycleContextBlock;
